import { c as _c } from "react/compiler-runtime";
// Данные и действия «Моих покупок»: список позиций (GET /buyer/purchases) +
// витринные карточки товаров (наличие для «В корзину»/«Похожие»), и
// сценарии строки — в корзину, возврат (модалка + успех), спор, поддержка.
// Общие для списка и страницы заказа.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type BuyerPurchase, type Product } from "@/api";
import { useCart, useProfile } from "@/store";
import { ReturnDisputeModal } from "./return-dispute-modal";
import { reviewDaysText } from "./return-parts";
import { ReturnAcceptedModal, ReturnRequestModal, type ReturnTarget } from "./return-request-modal";
import { orderQty } from "@/lib/min-order";
export function usePurchases() {
  const $ = _c(2);
  const userId = useProfile(_temp);
  let t0;
  if ($[0] !== userId) {
    t0 = {
      queryKey: ["buyer", userId, "purchases"],
      queryFn: _temp5
    };
    $[0] = userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
}
async function _temp5() {
  const items = await api.cabinet.purchases();
  const ids = [...new Set(items.map(_temp2))];
  const products = ids.length ? await api.products.byIds(ids).catch(_temp3) : [];
  return {
    items,
    products: new Map(products.map(_temp4))
  };
}
function _temp4(p) {
  return [p.id, p];
}
function _temp3() {
  return [] as Product[];
}
function _temp2(i) {
  return i.productId;
}
function _temp(s) {
  return s.user?.id ?? "guest";
}
export function usePurchaseActions() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const userId = useProfile(s => s.user?.id ?? "guest");
  const add = useCart(s_0 => s_0.add);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [returnTarget, setReturnTarget] = useState<ReturnTarget | null>(null);
  const [accepted, setAccepted] = useState<{
    photo?: string | null;
    quantity: number;
    days?: string | null;
  } | null>(null);
  const [disputeId, setDisputeId] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({
    queryKey: ["buyer", userId]
  });
  const toCart = async (item: BuyerPurchase, product?: Product) => {
    if (!product) return;
    const variants = product.variants?.filter(v => v.stock > 0) ?? [];
    const same = variants.find(v_0 => v_0.size === item.size && (!item.color || v_0.color === item.color));
    // Кладём тот же вариант, что покупали; если его нет, а выбор есть — на карточку.
    if (!same && (variants.length > 1 || !variants.length && product.sizes.length > 1 && !product.sizes.includes(item.size ?? ""))) {
      navigate(`/product/${product.id}`);
      return;
    }
    const v_1 = same ?? variants[0];
    setBusyId(item.orderLineId + item.orderId);
    try {
      await add({
        productId: product.id,
        variantId: v_1?.id,
        size: v_1?.size ?? item.size ?? product.sizes[0],
        color: v_1?.color ?? item.color ?? product.color,
        qty: orderQty(product)
      });
      toast.success("Товар в корзине");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось добавить в корзину");
    } finally {
      setBusyId(null);
    }
  };
  const startReturn = async (item_0: BuyerPurchase) => {
    try {
      const returnable = await api.cabinet.returnable(item_0.orderId);
      const line = returnable.lines.find(l => l.orderLineId === item_0.orderLineId);
      if (!line) {
        toast.error("Эту позицию уже нельзя вернуть");
        return;
      }
      setReturnTarget({
        orderId: item_0.orderId,
        line,
        brand: item_0.brand
      });
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось открыть заявку");
    }
  };
  const modals = <>
			<ReturnRequestModal target={returnTarget} onOpenChange={o => {
      if (!o) setReturnTarget(null);
    }} onCreated={summary => {
      const orderId = returnTarget?.orderId;
      setReturnTarget(null);
      void refresh();
      // Срок рассмотрения в тексте успеха — из только что созданной заявки.
      void api.cabinet.returns("active").then(list => list.find(x => x.orderId === orderId)).catch(() => undefined).then(created => setAccepted({
        ...summary,
        days: created ? reviewDaysText(created.createdAt, created.dueAt) : null
      }));
    }} />
			<ReturnAcceptedModal open={Boolean(accepted)} onOpenChange={o_0 => {
      if (!o_0) setAccepted(null);
    }} photo={accepted?.photo} quantity={accepted?.quantity ?? 1} days={accepted?.days} />
			<ReturnDisputeModal returnId={disputeId} onOpenChange={o_1 => {
      if (!o_1) setDisputeId(null);
    }} onOpened={() => {
      setDisputeId(null);
      void refresh();
      navigate("/profile/returns?tab=dispute");
    }} />
		</>;
  return {
    busyId,
    toCart,
    startReturn,
    openDispute: (item_1: BuyerPurchase) => item_1.returnId && setDisputeId(item_1.returnId),
    openSupport: () => navigate("/profile/support"),
    modals
  };
}